import type { SearchFilterCondition, SearchFilters } from 'lib-search-types';
import { isLeafFilterCondition } from 'lib-search-ui';
import type { Conversation } from 'lib-shared-types-api';

/**
 * Fields whose values we can resolve from the Conversation object.
 *
 * Conditions on any other fields (dates, text, custom attributes, etc.) are assumed to match since
 * they can't reliably be evaluated client-side.
 */
const evaluableConversationFields = [
  'initial_direction',
  'status',
  'agent_id',
  'initial_channel',
  'requested_by',
  'queue_id',
] as const;

type EvaluableConversationField = (typeof evaluableConversationFields)[number];

/**
 * Maps a search filter field name to its corresponding value on a
 * {@link Conversation} object. Only fields that can be reliably evaluated
 * client-side are mapped; all others return `undefined`.
 */
function getConversationFieldValue(
  conversation: Conversation,
  field: EvaluableConversationField,
): string | boolean | null | undefined {
  switch (field) {
    case 'initial_direction':
      return conversation.direction;
    case 'status':
      return conversation.status;
    case 'agent_id':
      return conversation.assignment?.agent_id;
    case 'initial_channel':
      return conversation.initial_channel;
    case 'requested_by':
      return conversation.requested_by;
    case 'queue_id':
      return conversation.queue?.id;
    default:
      return undefined;
  }
}

/**
 * Checks whether a conversation matches the active search filter conditions.
 *
 * Used by {@link useSearchConversationEventsEffect} to decide whether an incoming
 * WebSocket conversation event should update, remove, or be prepended to the
 * cached search results.
 *
 * Only leaf filter conditions are evaluated — nested filter groups are ignored.
 *
 * Returns:
 * - `'match'` — all conditions are evaluable and satisfied.
 * - `'no-match'` — at least one evaluable condition failed.
 * - `'indeterminate'` — evaluable conditions passed but non-evaluable conditions
 *   are present, so a definitive answer can't be given client-side.
 *
 * @param conversation The conversation from the WebSocket event.
 * @param filters The current search filters from Redux state.
 */
export type FilterMatchResult = 'match' | 'no-match' | 'indeterminate';

export function evaluateConversationAgainstSearchFilters(
  conversation: Conversation,
  filters: SearchFilters<'conversations'>,
): FilterMatchResult {
  const conditions = filters.filters.filter(isLeafFilterCondition);

  if (conditions.length === 0) {
    return 'match';
  }

  let hasNonEvaluable = false;

  const evaluate = (
    condition: SearchFilterCondition<'conversations'>,
  ): boolean => {
    if (
      !evaluableConversationFields.includes(
        condition.field as EvaluableConversationField,
      )
    ) {
      hasNonEvaluable = true;
      return true;
    }

    const fieldValue = getConversationFieldValue(
      conversation,
      condition.field as EvaluableConversationField,
    );
    const values = condition.values.map(({ value }) => value);

    switch (condition.operator) {
      case 'is_one':
        return values.includes(fieldValue as string);
      case 'is_not_one':
        return !values.includes(fieldValue as string);
      case 'is_empty':
        return fieldValue == null || fieldValue === '';
      case 'is_not_empty':
        return fieldValue != null && fieldValue !== '';
      default:
        return true;
    }
  };

  const allPass =
    filters.strategy === 'any'
      ? conditions.some(evaluate)
      : conditions.every(evaluate);

  if (!allPass) {
    return 'no-match';
  }

  return hasNonEvaluable ? 'indeterminate' : 'match';
}
